/**
 * condoDocumentsModel.js — pure derivations over `CondoDocumentsSummaryOut`,
 * kept out of the components so they stay presentational.
 *
 * Shapes are the Pydantic models in backend/app/routes/condo_documents.py:
 *
 * - `CondoDocumentsSummaryOut`: `{ viewer_role: 'seller'|'buyer'|'other',
 *   rule, small_condominium: bool|null, small_condominium_question:
 *   {text, option_yes, option_no}, items: [ChecklistItemOut],
 *   packages: [PackageOut], deadline: DeadlineOut|null, clock: ClockOut,
 *   warnings: [WarningOut], closing_date, attestations:
 *   {seller_delivery: {version, text}, buyer_receipt: {version, text, text_missing}} }`.
 * - `ChecklistItemOut`: `{code, label, statute, required, na_eligible, na_preset}`.
 *   The list is already the checklist for the transaction's variant (full or
 *   small condominium), so the client never filters it.
 * - `PackageOut`: `{ id, sequence, kind, status, small_condominium,
 *   checklist_variant, responds_to_package_id, required_items, missing_items,
 *   items: {code: {not_applicable, note}}, files: [FileOut], delivered_at,
 *   delivery_notice: {accepted, esign_consent, at}, request_by_local_date,
 *   buyer_received_at, buyer_receipt: {all_received, missing_items,
 *   request_timely, …}, missing_request_at, cure_deadline_at,
 *   review_anchor_at, review_deadline_local_date, review_rule_id,
 *   deadline: DeadlineOut|null, … }`.
 * - `DeadlineOut`: `{ package_id, kind, local_date, at,
 *   buyer_act_by_local_date, seller_latest_local_date, ambiguous_local_dates,
 *   anchor_at, anchor_source: 'delivery'|'buyer_acknowledgement'|'after_cure',
 *   rule_id, calendar_version, time_zone, time_zone_label }`. `time_zone`
 *   is an IANA zone ("America/Chicago"); `time_zone_label` is display text
 *   ("Central time"). `summary.deadline` (mirrored in
 *   `clock.deadline`) is the latest window; null until computed or when the
 *   calendar doesn't cover the year.
 * - `LateMissingRequestOut` (`clock.late_missing_request`): `{ package_id,
 *   missing_items, requested_at, requested_local_date, request_by_local_date,
 *   answered_by_package_id, answered_at, deliver_by_local_date }`. A request
 *   made after the (3c) period: the seller may answer it with a supplement,
 *   and the buyer's deadline doesn't move.
 * - `ClockOut`: `{ status, not_started_reason: 'not_delivered'|
 *   'delivery_unconfirmed'|'awaiting_acknowledgement'|
 *   'awaiting_missing_documents'|null, received_at, review_deadline_local_date,
 *   rule_id, deadline, open_missing_request: {package_id, missing_items,
 *   requested_at, cure_deadline_at, cure_deadline_local_date}|null,
 *   late_missing_request: LateMissingRequestOut|null }`.
 * - `WarningOut`: `{code, severity, message, date, deliver_by_local_date, delivered_at}`.
 *
 * Which event starts the clock (legal §5.3): delivery to the buyer's account
 * when the buyer's E-SIGN consent is on file and the email notice was
 * accepted; otherwise the buyer's acknowledgement. `deadline.anchor_source`
 * says which one the server used.
 */

import {
  ANCHOR_SOURCE_ACK,
  ANCHOR_SOURCE_AFTER_CURE,
  ANCHOR_SOURCE_FIRST_VIEW,
  CLOCK_STATUS_ENDED,
  CLOCK_STATUS_PENDING_CALENDAR,
  CLOCK_STATUS_RUNNING,
  NOT_STARTED_AWAITING_ACK,
  NOT_STARTED_AWAITING_MISSING,
  NOT_STARTED_DELIVERY_UNCONFIRMED,
  NOT_STARTED_NOT_DELIVERED,
  PACKAGE_KIND_AMENDMENT,
  PACKAGE_KIND_INITIAL,
  PACKAGE_KIND_SUPPLEMENT,
  PACKAGE_STATUS_DELIVERED,
  PACKAGE_STATUS_DRAFT,
  PACKAGE_STATUS_RECEIVED,
  VIEWER_ROLE_BUYER,
  VIEWER_ROLE_OTHER,
  VIEWER_ROLE_SELLER,
} from '../../agents/condoDocumentsAgent';
import { addCalendarDays, resolveTimeZone, weekdayOnOrBefore } from './condoDocumentsFormat';
import {
  CONDO_ITEM_LABELS,
  CONDO_ITEM_NA_PRESETS,
  CONDO_NA_PRESET_BUDGET_SMALL_CONDO,
  CONDO_WARNING_COPY,
} from './condoDocumentsCopy';

export const ROLE_SELLER = VIEWER_ROLE_SELLER;
export const ROLE_BUYER = VIEWER_ROLE_BUYER;

/** Mirrors the server's upload rules (arch §7.3: same as the contingency upload). */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
export const ACCEPTED_UPLOAD_TYPES = 'application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png';

export const CHECKLIST_VARIANT_FULL = 'full';
export const CHECKLIST_VARIANT_SMALL = 'small_condominium';

/** The server label wins; the constants module is the fallback. */
export function itemLabel(item) {
  if (!item) return '';
  return item.label || CONDO_ITEM_LABELS[item.code] || item.code;
}

export function labelForCode(code, items = []) {
  return itemLabel(items.find((item) => item.code === code) || { code });
}

export function sortedPackages(summary) {
  return [...(summary?.packages || [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
}

export function filesForItem(pkg, code) {
  return (pkg?.files || []).filter((file) => file.item_code === code);
}

export function itemEntry(pkg, code) {
  return pkg?.items?.[code] || {};
}

export function isItemCovered(pkg, code) {
  return filesForItem(pkg, code).length > 0 || itemEntry(pkg, code).not_applicable === true;
}

export function isDelivered(pkg) {
  return pkg?.status === PACKAGE_STATUS_DELIVERED || pkg?.status === PACKAGE_STATUS_RECEIVED;
}

export function hasReportedMissing(pkg) {
  return pkg?.buyer_receipt?.all_received === false && (pkg.buyer_receipt.missing_items || []).length > 0;
}

/**
 * A missing-documents report made after the § 703.33(3c) request period
 * (`buyer_receipt.request_timely === false`). It is recorded and forwarded to
 * the seller, but it doesn't change the buyer's review period (L4 §9.5).
 */
export function isLateMissingRequest(pkg) {
  return hasReportedMissing(pkg) && pkg.buyer_receipt.request_timely === false;
}

/** The package the seller is working on (the latest draft), if any. */
export function openDraft(packages) {
  const drafts = packages.filter((pkg) => pkg.status === PACKAGE_STATUS_DRAFT);
  return drafts.length ? drafts[drafts.length - 1] : null;
}

/**
 * The latest package whose buyer reported missing documents and has not yet
 * been followed by a supplement package (draft or delivered).
 */
export function openMissingRequest(packages) {
  for (let i = packages.length - 1; i >= 0; i -= 1) {
    const pkg = packages[i];
    if (pkg.kind === PACKAGE_KIND_SUPPLEMENT) return null;
    // `answered_by_package_id`: a supplement already answers it (draft or delivered).
    if (pkg.answered_by_package_id != null) return null;
    if (hasReportedMissing(pkg)) return pkg;
  }
  return null;
}

/** `clock.late_missing_request` for this package, when the server sent it. */
export function lateRequestFor(summary, pkg) {
  const late = summary?.clock?.late_missing_request;
  return late && pkg && late.package_id === pkg.id ? late : null;
}

export function initialPackage(packages) {
  return packages.find((pkg) => pkg.kind === PACKAGE_KIND_INITIAL) || null;
}

/** Whether a package uses the small-condominium checklist (§ 703.365(8)). */
export function isSmallCondoPackage(pkg, packages = []) {
  if (typeof pkg?.small_condominium === 'boolean') return pkg.small_condominium;
  if (pkg?.checklist_variant) return pkg.checklist_variant === CHECKLIST_VARIANT_SMALL;
  const initial = initialPackage(packages);
  return Boolean(initial && initial !== pkg && isSmallCondoPackage(initial));
}

/** The seller may mark this item Not applicable (the server decides). */
export function isNaEligible(item) {
  return item?.na_eligible !== false;
}

/** The note a "Not applicable" form starts with: the server's preset, else legal §5.2's. */
export function notApplicablePreset(item, small = false) {
  if (item?.na_preset) return item.na_preset;
  if (item?.code === 'budget' && small) return CONDO_NA_PRESET_BUDGET_SMALL_CONDO;
  return CONDO_ITEM_NA_PRESETS[item?.code] || '';
}

/**
 * The checklist rows for a package. `summary.items` is already the checklist
 * for the transaction's variant:
 * - initial: every item, `required` as the server says;
 * - supplement: only the items the buyer reported missing, all required;
 * - amendment: every item, none required (it amends whichever changed).
 */
export function checklistForPackage(pkg, summary, packages) {
  const items = summary?.items || [];
  if (pkg?.kind === PACKAGE_KIND_SUPPLEMENT) {
    const parent = packages.find((p) => p.id === pkg.responds_to_package_id)
      || openMissingRequest(packages.filter((p) => (p.sequence ?? 0) < (pkg.sequence ?? 0)));
    const missing = new Set(parent?.buyer_receipt?.missing_items || []);
    const subset = items.filter((item) => missing.has(item.code));
    return (subset.length ? subset : items).map((item) => ({ ...item, required: true }));
  }
  if (pkg?.kind === PACKAGE_KIND_AMENDMENT) {
    return items.map((item) => ({ ...item, required: false }));
  }
  return items;
}

/** Progress and whether the package can be delivered (before the attestation). */
export function packageProgress(pkg, checklist) {
  const required = checklist.filter((item) => item.required);
  const done = required.filter((item) => isItemCovered(pkg, item.code)).length;
  const fileCount = (pkg?.files || []).length;
  const ready = pkg?.kind === PACKAGE_KIND_AMENDMENT
    ? fileCount > 0
    : done === required.length;
  return { done, total: required.length, remaining: required.length - done, fileCount, ready };
}

/** The last day to deliver at least N calendar days before closing (legal §5.4), unadjusted. */
export function rawDeliverByDate(summary) {
  const closing = summary?.closing_date;
  if (!closing) return null;
  const minDays = summary?.rule?.min_days_before_closing ?? 15;
  return addCalendarDays(closing, -minDays);
}

/**
 * The deliver-by date to show (legal §5.4): the server's holiday-aware date
 * when it sends one, else closing − 15 calendar days moved back off a weekend.
 */
export function deliverByDate(summary, warning = null) {
  const fromServer = warning?.deliver_by_local_date || summary?.deliver_by_local_date;
  if (fromServer) return fromServer;
  const raw = rawDeliverByDate(summary);
  return raw ? weekdayOnOrBefore(raw) : null;
}

/** The latest review window: `summary.deadline`, mirrored in `clock.deadline`. */
export function currentDeadline(summary) {
  return summary?.deadline ?? summary?.clock?.deadline ?? null;
}

/**
 * The deadline dates to show (decision b). `primary` is the stored deadline.
 * The buyer is told to act by `buyerActBy` and the seller that it could end
 * as late as `sellerLatest`; either is null when the calendar can't cover it,
 * and then that hedge is simply not shown.
 */
export function deadlineDates(summary) {
  const clock = summary?.clock || {};
  const deadline = currentDeadline(summary);
  return {
    primary: deadline?.local_date || clock.review_deadline_local_date || null,
    buyerActBy: deadline?.buyer_act_by_local_date || null,
    sellerLatest: deadline?.seller_latest_local_date || null,
    ambiguousDates: Array.isArray(deadline?.ambiguous_local_dates) ? deadline.ambiguous_local_dates : [],
    anchorSource: deadline?.anchor_source || null,
    anchorAt: deadline?.anchor_at || null,
    ruleId: deadline?.rule_id || clock.rule_id || null,
    kind: deadline?.kind || null,
    packageId: deadline?.package_id ?? null,
    // `time_zone` is the IANA zone for Intl; `time_zone_label` is the text
    // shown in the sentences. A zone Intl rejects falls back to the property
    // default rather than throwing (an older server sent the label here).
    timeZone: resolveTimeZone(deadline?.time_zone),
    timeZoneLabel: deadline?.time_zone_label || null,
  };
}

export const DEADLINE_KIND_INITIAL = 'initial';
export const DEADLINE_KIND_AFTER_CURE = 'after_cure';
export const DEADLINE_KIND_AMENDMENT = 'amendment';

/** Which buyer sentence the deadline needs: `initial` | `after_cure` | `amendment`. */
export function deadlineKind(dates) {
  if (dates.anchorSource === ANCHOR_SOURCE_AFTER_CURE) return DEADLINE_KIND_AFTER_CURE;
  if (dates.kind === PACKAGE_KIND_AMENDMENT) return DEADLINE_KIND_AMENDMENT;
  const suffix = String(dates.ruleId || '').split('.').pop();
  if (suffix === DEADLINE_KIND_AFTER_CURE || suffix === DEADLINE_KIND_AMENDMENT) return suffix;
  return DEADLINE_KIND_INITIAL;
}

/** True when the window was counted from the buyer's acknowledgement, not delivery. */
export function isAcknowledgementAnchored(dates) {
  return dates.anchorSource === ANCHOR_SOURCE_ACK;
}

/** True when the window was counted from the buyer's first open or download of a file. */
export function isFirstViewAnchored(dates) {
  return dates.anchorSource === ANCHOR_SOURCE_FIRST_VIEW;
}

/** The package the displayed deadline belongs to (`deadline.package_id`). */
export function deadlinePackage(summary, packages) {
  const { packageId } = deadlineDates(summary);
  const delivered = packages.filter(isDelivered);
  if (packageId != null) {
    const match = packages.find((pkg) => pkg.id === packageId);
    if (match) return match;
  }
  return initialPackage(delivered) || delivered[0] || null;
}

/**
 * How the clock card should render:
 * - `hidden`: nothing delivered yet (`not_delivered`);
 * - `pending`: no deadline came back (the holiday calendar doesn't cover the
 *   year) — show the fallback, never a date;
 * - `running` / `ended`: a stored deadline;
 * - `awaiting_missing`: delivered, and the window waits on the seller's cure of
 *   a timely missing-documents request (`awaiting_missing_documents`);
 * - `not_started`: delivered, but the clock waits for the buyer's
 *   acknowledgement (`delivery_unconfirmed` or `awaiting_acknowledgement`).
 *
 * Every view but `hidden` is for a delivered package, so none of them may say
 * the documents "haven't been delivered" (L4 §9.4).
 */
/**
 * Text for the "buyer hasn't confirmed receipt" warning. While the review
 * period waits on the buyer (clock `not_started`), it is not counted from
 * delivery, so only the first sentence applies.
 */
export function receiptUnconfirmedText(summary, packages, deliveredDate) {
  if (clockView(summary, packages) === 'not_started') {
    return CONDO_WARNING_COPY.condo_receipt_unconfirmed_not_started();
  }
  return CONDO_WARNING_COPY.condo_receipt_unconfirmed({ deliveredDate });
}

export function clockView(summary, packages) {
  const clock = summary?.clock || {};
  const hasDeadline = Boolean(deadlineDates(summary).primary);
  if (clock.status === CLOCK_STATUS_PENDING_CALENDAR) return 'pending';
  if (hasDeadline) return clock.status === CLOCK_STATUS_ENDED ? 'ended' : 'running';
  if (clock.status === CLOCK_STATUS_RUNNING || clock.status === CLOCK_STATUS_ENDED) return 'pending';
  switch (clock.not_started_reason) {
    case NOT_STARTED_NOT_DELIVERED:
      return 'hidden';
    case NOT_STARTED_AWAITING_MISSING:
      return 'awaiting_missing';
    case NOT_STARTED_DELIVERY_UNCONFIRMED:
    case NOT_STARTED_AWAITING_ACK:
      return 'not_started';
    default:
      break;
  }
  // No reason given: derive it from the packages.
  if (!packages.some(isDelivered)) return 'hidden';
  if (clock.open_missing_request) return 'awaiting_missing';
  const request = openMissingRequest(packages);
  // A late request doesn't hold the window (§ 703.33(3c); L4 §9.5).
  const late = request && (isLateMissingRequest(request) || clock.late_missing_request?.package_id === request.id);
  if (request && !late) return 'awaiting_missing';
  if (clock.received_at) return 'pending';
  return 'not_started';
}

/**
 * The seller's cure date for a missing-documents request: the server's local
 * date for the open request, else the package's cure timestamp.
 */
export function cureDate(summary, pkg) {
  const open = summary?.clock?.open_missing_request;
  if (open && open.package_id === pkg?.id && open.cure_deadline_local_date) {
    return { localDate: open.cure_deadline_local_date, at: null };
  }
  return { localDate: null, at: pkg?.cure_deadline_at || null };
}

/**
 * Who is looking. The summary's `viewer_role` wins ('other' is a read-only
 * admin); before the summary loads, an explicit role, then the viewer's id
 * matched against the transaction's parties.
 */
export function resolveViewerRole({ viewerRole, viewerId, transaction, summaryRole } = {}) {
  if (summaryRole === ROLE_SELLER || summaryRole === ROLE_BUYER) return summaryRole;
  if (summaryRole === VIEWER_ROLE_OTHER) return null;
  if (viewerRole === ROLE_SELLER || viewerRole === ROLE_BUYER) return viewerRole;
  if (viewerId == null || !transaction) return null;
  if (String(transaction.seller_id) === String(viewerId)) return ROLE_SELLER;
  if (String(transaction.buyer_id) === String(viewerId)) return ROLE_BUYER;
  return null;
}
